import { PlusIcon, SearchIcon } from "@heroicons/react/outline";
import { Breadcrumbs } from "components/breadcrumbs";
import Button from "components/button";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EwaiverType, useEwaiver } from "../hooks/useEwaiver";
import Input from "components/input";
import Select from "components/select";
import { Label } from "components/label";
import { useSearchParams } from "react-router-dom";
import useSetQueryParams from "routes/wps/attendees/hooks/useGetQueryParams";
import Pagination from "components/pagination";
import { EwaiverDataTable } from "../components/dataTable";
import { FullScreenLoading } from "components/fullScreenLoading";
import { useUpdateEwaiverStatus } from "../hooks/useUpdateEwaiverStatus";
import toast from "react-hot-toast";
import useWindowWidth from "utills/InitWidthDevice";
import ListCard from "../components/listCard";
import { TrackingModal } from "../components/TrackingModal";
import { isHaveActionPermission } from "utills/helpers";

const EwaiverListPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setQueryParams = useSetQueryParams();
  const windowWidth = useWindowWidth();

  const searchQuery = searchParams.get("q");
  const status = searchParams.get("is_active");

  const STATUS_OPTIONS = [
    { value: "", label: "All" },
    { value: "true", label: "Active" },
    { value: "false", label: "Inactive" },
  ];

  const [isMobile, setIsMobile] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [filterGroup, setFilterGroup] = useState({
    query: searchQuery ?? "",
    status: status ?? "",
  });
  const [selectedItem, setSelectedItem] = useState<EwaiverType | null>(null);
  const [openTrackingModal, setOpenTrackingModal] = useState<boolean>(false);

  const { fetchData, loading, data, query, setQuery, paging } = useEwaiver(
    page,
    {
      initQuery: {
        // limit: 10,
        q: searchQuery ?? "",
        is_active: status ?? "",
      },
    }
  );
  const { updateStatus } = useUpdateEwaiverStatus();

  const handleNavigateCreate = () => {
    navigate("/ewaiver/create");
  };

  const handleChangePage = (page: number) => {
    setPage(page);
  };

  const handleToggleActive = async (item: EwaiverType) => {
    const confirmText =
      item?.is_have_online_checkin === 1 ? "deactivate" : "activate";

    if (
      window.confirm(
        `Are you sure you want to ${confirmText} the eWaiver for this event?`
      )
    ) {
      try {
        const newStatus = item?.is_have_online_checkin ? 0 : 1;

        const updateStatusRes = await updateStatus(
          item?.event_id,
          newStatus ? true : false
        );

        console.log("Update status res:", updateStatusRes);

        fetchData();
        toast.success("Update successfully");
      } catch (error) {
        console.log("Update ewaiver status failed:", error);

        toast.error("Update ewaiver status failed");
      }
    }
  };

  const handleUpdateFilter = (value: string, key: string) => {
    const dataFilter = { ...filterGroup } as any;

    dataFilter[key] = value;
    setFilterGroup({ ...dataFilter });
  };

  const handleApplyFillter = (event: any) => {
    if (event.key === "Enter" || event.key === "Search") {
      console.log("Apply filter:", filterGroup);

      setPage(1);

      setQueryParams({
        q: filterGroup?.query ?? "",
        is_active: filterGroup?.status ?? "",
      });

      setQuery({
        q: filterGroup?.query ?? "",
        is_active: filterGroup?.status ?? "",
      });
    }
  };

  const handleApplyAction = (type: string, value: EwaiverType) => {
    console.log("Apply action:", value);
    setSelectedItem(value);

    if (type === "tracking") setOpenTrackingModal(true);
  };

  useEffect(() => {
    if (windowWidth && windowWidth < 1024) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [windowWidth]);

  return (
    <div className="wrapper">
      {loading && <FullScreenLoading />}

      {selectedItem && (
        <TrackingModal
          item={selectedItem}
          isMobile={isMobile}
          open={openTrackingModal}
          onClose={() => {
            setOpenTrackingModal(false);
            setSelectedItem(null);
          }}
        />
      )}
      <Breadcrumbs
        {...(isHaveActionPermission("", "e_waiver", "create") && {
          additionalLabel: (
            <div className="flex items-center gap-x-3">
              <Button
                className="w-11 h-11 sm:w-auto"
                onClick={handleNavigateCreate}
              >
                <span className="sr-only">Add New eWaiver</span>
                <PlusIcon className="w-4 h-4 text-white" />{" "}
                <span className="hidden sm:block sm:ml-2">Add New eWaiver</span>
              </Button>
            </div>
          ),
        })}
      />

      <div className="box-container">
        <div className="w-full flex flex-wrap items-end gap-3">
          <div className="w-full md:w-[600px]">
            <Label id="name" name="Name" />
            <div className="mt-2">
              <Input
                className="px-2 py-2"
                label="Search"
                id="name"
                type="text"
                placeholder="Search by Event Name"
                value={filterGroup.query}
                onChange={(e: any) => {
                  handleUpdateFilter(e.target.value, "query");
                }}
                onKeyDown={handleApplyFillter}
              />
            </div>
          </div>

          <div className="w-[200px]">
            <Label id="is_active" name="Status" />
            <div className="mt-2">
              <Select
                options={STATUS_OPTIONS}
                isClearable={true}
                id="is_active"
                placeholder="Select Status"
                className="py-0.5"
                value={STATUS_OPTIONS.find(
                  (d) => d.value === filterGroup.status
                )}
                onChange={(selected: { label: string; value: string }) => {
                  handleUpdateFilter(selected?.value, "status");
                }}
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-[100px] bg-white rounded-md px-3 text-brand-500 py-2
                        border-solid border-brand-500 border-[1px] hover:bg-white active:bg-white active:opacity-85"
            onClick={() => {
              handleApplyFillter({ key: "Search" });
            }}
          >
            <SearchIcon className="w-3 h-4 text-brand-500 mr-2" /> Search
          </Button>
        </div>
      </div>

      {isMobile ? (
        <div className="w-full flex flex-col gap-[8px]">
          {data?.map((item: EwaiverType, idx: number) => {
            return (
              <ListCard
                index={idx}
                key={item?.event_id}
                item={item}
                pageSize={10}
                currentPage={paging?.current_page}
                _toggleActive={handleToggleActive}
              />
            );
          })}

          <Pagination
            currentPage={paging?.current_page}
            totalPage={paging?.total_page}
            total={{
              totalItems: paging?.total_item ?? 0,
              total: paging?.total ?? 0,
            }}
            onChange={handleChangePage}
          />
        </div>
      ) : (
        <div className="box-container">
          <div className="mb-5">
            <EwaiverDataTable
              data={data}
              pageSize={10}
              currentPage={paging?.current_page}
              _toggleActive={handleToggleActive}
              handleApplyAction={handleApplyAction}
            />
          </div>

          <Pagination
            currentPage={paging?.current_page}
            totalPage={paging?.total_page}
            total={{
              totalItems: paging?.total_item ?? 0,
              total: paging?.total ?? 0,
            }}
            onChange={handleChangePage}
          />
        </div>
      )}
    </div>
  );
};

export default EwaiverListPage;
